/**
 * «Невозвраты» — `GET /api/nonreturn/report`, `GET /api/nonreturn/list`.
 * Спека `docs/specs/2026-10-08-nonreturn-design.md`.
 */
import { apiFetch, query } from "@/lib/api";

/** Вещей и сделок. */
export type Count = { items: number; deals: number };

/** Закончившиеся прокаты месяца: не вернули до сих пор, вернули позже срока на > 3 и > 14 дней. */
export type Numbers = { ended: number; open: number; open_deals: number; late3: number; late14: number };

export type Now = {
  overdue: Count;
  age: { d3: Count; d14: Count; d30: Count; more: Count };
  closed: Count;
  renting: Count;
  no_date: Count;
};

export type City = Numbers & {
  city: string;
  /** Город как в базе — им отбирают; пусто у «без города». */
  code: string;
  before: Count;
  now: Count;
  /** Числа по ключу месяца; пустых месяцев в объекте нет. */
  months: Record<string, Numbers>;
};

/** Ровно то, что отдаёт `GET /api/nonreturn/report`. */
export type NonreturnReport = {
  from: string;
  to: string;
  today: string;
  dress: boolean;
  city: string | null;
  late_days: [number, number];
  now: Now;
  months: (Numbers & { key: string; label: string })[];
  before: Count;
  total: Numbers;
  cities: City[];
  synced: string | null;
};

export type Kind = "open" | "old" | "closed" | "renting" | "no_date" | "late3" | "late14";

export type Deal = {
  deal: number;
  city: string;
  customer: string;
  phone: number | null;
  stage: string;
  stage_name: string;
  closed: boolean;
  rent_start: string | null;
  rent_end: string | null;
  /** На руках — дней просрочки на сегодня; вернули — на сколько опоздали. */
  days: number | null;
  /** Переписка клиента в «Чатах»: чат сделки, иначе чат номера договора. */
  chat: { id: number; channel: string } | null;
  items: { serial: number; name: string; dress: boolean; issued_at: string; returned_at: string | null; days: number | null }[];
};

export type NonreturnList = { kind: Kind; items: number; total: number; limit: number; deals: Deal[] };

export type Filter = { from?: string | undefined; to?: string | undefined; city?: string | undefined; all: boolean };

export function loadNonreturn(f: Filter): Promise<NonreturnReport> {
  return apiFetch<NonreturnReport>(`/api/nonreturn/report${query({ from: f.from, to: f.to, city: f.city, all: f.all })}`);
}

export function loadNonreturnList(kind: Kind, f: Filter): Promise<NonreturnList> {
  return apiFetch<NonreturnList>(`/api/nonreturn/list${query({ kind, from: f.from, to: f.to, city: f.city, all: f.all })}`);
}

/** Стадия цепочки «невозврат → руководителю салона». */
export type NoticeState = "sent" | "answered" | "returned" | "failed";

export type NoticeRow = {
  deal: number;
  dogovor: number;
  city: string;
  group: string;
  state: NoticeState;
  passport: boolean;
  rent_end: string | null;
  sent: string | null;
  asked: string | null;
  asks: number;
  answers: number;
  returned: string | null;
  error: string;
  /** Последний ответ руководителя; `comment` — уже лёг комментарием в сделку. */
  reply: { name: string; text: string; at: string; comment: boolean } | null;
};

/** `GET /api/nonreturn/notice` — спека `docs/specs/2026-10-23-nonreturn-notice-design.md`. */
export type NonreturnNotice = {
  funnel: { overdue: number; sent: number; answered: number; asked: number; returned: number; waiting: number };
  /** Невозвраты за 30 дней, которым ещё не писали. */
  pending: number[];
  rows: NoticeRow[];
};

export function loadNonreturnNotice(): Promise<NonreturnNotice> {
  return apiFetch<NonreturnNotice>("/api/nonreturn/notice");
}
